import React from "react";
import "./Skeleton.css"

// Enhanced Skeleton Component with attractive variants
const Skeleton = ({ 
  width = "100%", 
  height = "16px", 
  borderRadius = "6px",
  className = "",
  variant = "text", // text, rectangular, circular
  animation = "shimmer", // shimmer, glow, luxury, wave, pulse-gradient, premium
  speed = "normal" // fast, normal, slow
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case "circular":
        return {
          borderRadius: "50%",
          width: height, // Make it a circle
        };
      case "rectangular":
        return {
          borderRadius: "12px",
        };
      default:
        return {
          borderRadius,
        };
    }
  };

  const getSpeedClass = () => {
    switch (speed) {
      case "fast":
        return "fast";
      case "slow":
        return "slow";
      default:
        return "";
    }
  };

  return (
    <div
      className={`skeleton-loader ${animation} ${getSpeedClass()} ${className}`}
      style={{
        width,
        height,
        ...getVariantStyles(),
      }}
    />
  );
};

// Enhanced Table Skeleton with modern styling
const TableRowSkeleton = ({ columns = 12, rows = 5, animation = "wave" }) => {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={rowIndex} className="skeleton-row">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td key={colIndex}>
              <Skeleton 
                height="24px"
                animation={animation}
                width={
                  colIndex === 0 ? "30px" : // Actions column
                  colIndex === 1 ? "85px" : // Trip number
                  colIndex === 2 ? "95px" : // Date
                  colIndex === 3 ? "130px" : // Party
                  colIndex === 4 ? "110px" : // From station
                  colIndex === 5 ? "110px" : // To station
                  colIndex === 6 ? "95px" : // End date
                  colIndex === 7 ? "85px" : // Branch
                  colIndex === 8 ? "120px" : // Vehicle
                  colIndex === 9 ? "110px" : // Driver/Supplier
                  colIndex === 10 ? "75px" : // Status
                  "45px" // View column
                }
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

// Premium Card Skeleton with enhanced styling
const CardSkeleton = ({ 
  showAvatar = false, 
  lines = 3, 
  animation = "luxury",
  variant = "premium" 
}) => {
  return (
    <div className="skeleton-card">
      <div style={{ padding: "20px" }}>
        {showAvatar && (
          <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
            <Skeleton 
              variant="circular" 
              height="48px" 
              animation="glow"
            />
            <div style={{ marginLeft: "16px", flex: 1 }}>
              <Skeleton 
                height="18px" 
                width="65%" 
                animation={animation}
                style={{ marginBottom: "8px" }}
              />
              <Skeleton 
                height="14px" 
                width="45%" 
                animation={animation}
              />
            </div>
          </div>
        )}
        {Array.from({ length: lines }).map((_, index) => (
          <Skeleton 
            key={index}
            height="16px" 
            width={
              index === 0 ? "100%" : 
              index === lines - 1 ? "75%" : 
              "90%"
            }
            animation={animation}
            style={{ marginBottom: "10px" }}
          />
        ))}
      </div>
    </div>
  );
};

// Modern List Skeleton
const ListSkeleton = ({ 
  items = 5, 
  showAvatar = true, 
  animation = "shimmer" 
}) => {
  return (
    <div className="skeleton-list">
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} className="skeleton-list-item" style={{ 
          display: "flex", 
          alignItems: "center", 
          padding: "16px 20px"
        }}>
          {showAvatar && (
            <Skeleton 
              variant="circular" 
              height="40px" 
              animation="glow"
              style={{ marginRight: "16px" }} 
            />
          )}
          <div style={{ flex: 1 }}>
            <Skeleton 
              height="18px" 
              width="75%" 
              animation={animation}
              style={{ marginBottom: "8px" }}
            />
            <Skeleton 
              height="14px" 
              width="55%" 
              animation={animation}
            />
          </div>
          <Skeleton 
            height="36px" 
            width="90px" 
            borderRadius="8px"
            animation="glow"
          />
        </div>
      ))}
    </div>
  );
};

// Enhanced Button Skeleton
const ButtonSkeleton = ({ 
  width = "100px", 
  height = "40px", 
  animation = "glow",
  variant = "modern" 
}) => {
  return (
    <Skeleton 
      width={width} 
      height={height} 
      borderRadius="8px"
      animation={animation}
      className={variant}
    />
  );
};

// Profile Skeleton Component
const ProfileSkeleton = ({ animation = "luxury" }) => {
  return (
    <div className="skeleton-card">
      <div style={{ padding: "24px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: "24px" }}>
          <Skeleton 
            variant="circular" 
            height="80px" 
            animation="glow"
          />
          <div style={{ marginLeft: "20px", flex: 1 }}>
            <Skeleton 
              height="24px" 
              width="60%" 
              animation={animation}
              style={{ marginBottom: "8px" }}
            />
            <Skeleton 
              height="16px" 
              width="40%" 
              animation={animation}
              style={{ marginBottom: "8px" }}
            />
            <Skeleton 
              height="14px" 
              width="70%" 
              animation={animation}
            />
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
          {[1, 2, 3].map((_, index) => (
            <div key={index} style={{ flex: 1, textAlign: "center" }}>
              <Skeleton 
                height="20px" 
                width="80%" 
                animation="glow"
                style={{ marginBottom: "4px", margin: "0 auto" }}
              />
              <Skeleton 
                height="14px" 
                width="60%" 
                animation={animation}
                style={{ margin: "0 auto" }}
              />
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: "12px" }}>
          <ButtonSkeleton width="120px" height="44px" animation="premium" />
          <ButtonSkeleton width="100px" height="44px" animation="glow" />
        </div>
      </div>
    </div>
  );
};

// Text Block Skeleton
const TextSkeleton = ({ 
  lines = 3, 
  animation = "wave",
  spacing = "normal" // tight, normal, loose
}) => {
  const getSpacing = () => {
    switch (spacing) {
      case "tight":
        return "6px";
      case "loose":
        return "16px";
      default:
        return "10px";
    }
  };

  return (
    <div>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton 
          key={index}
          height="16px" 
          width={
            index === 0 ? "100%" :
            index === lines - 1 ? "80%" :
            "95%"
          }
          animation={animation}
          style={{ marginBottom: getSpacing() }}
        />
      ))}
    </div>
  );
};

export default Skeleton;
export { 
  TableRowSkeleton, 
  CardSkeleton, 
  ListSkeleton, 
  ButtonSkeleton,
  ProfileSkeleton,
  TextSkeleton
};